import "@shoelace-style/shoelace/dist/components/icon/icon.js";

import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../BaseElement";
import { litIf } from "../LitUtils";
import { styles } from "./PlaceMarker.style";
import { LocationType } from "../../../models";
import { watch } from "../internal/watch";
import {
    getPlaceDiveRating,
    getPlaceWarnings,
    getPlaceWaterDepthText,
    getPlaceWaterTempText,
} from "../../PlaceUtils";
import uniq from "lodash-es/uniq.js";
import { WarningLevel } from "../../../API";
import { ChangeSet, ChangeSetListener } from "../../DataStore/ChangeSet";
import { DataProvider } from "../../DataStore/DataProvider";
import { DataClient } from "../../DataStore/LocalDataStore";
import { getWarning } from "../../backend/AllWarnings";

import "../Warning/WarningIcon";

export enum MarkerColor {
    Red = "red",
    LightRed = "lightred",
    DarkRed = "darkred",
    Orange = "orange",
    Beige = "beige",

    Green = "green",
    LightGreen = "lightgreen",
    DarkGreen = "darkgreen",

    Blue = "blue",
    LightBlue = "lightblue",
    DarkBlue = "darkblue",
    Purple = "purple",
    DarkPurple = "darkpurple",
    Pink = "pink",
    CadetBlue = "cadetblue",
    White = "white",
    Gray = "gray",
    LightGray = "lightgray",
    Black = "black",
    Brown = "brown",
}

export const LocationTypeToColor = new Map<LocationType, string>([
    [LocationType.UNKNOWN, MarkerColor.Red],
    [LocationType.DEPRESSION, MarkerColor.LightGreen],
    [LocationType.NOT_FOUND, MarkerColor.Black],
    [LocationType.CAVE, MarkerColor.DarkGreen],
    [LocationType.SINKHOLE, MarkerColor.Blue],
    [LocationType.SOLUTION_TUBE, MarkerColor.DarkGreen],
    [LocationType.ROCKY_OUTCROP, MarkerColor.Purple],
    [LocationType.DESTROYED, MarkerColor.Black],
    [LocationType.LANDMARK, MarkerColor.CadetBlue],
    [LocationType.MINE, MarkerColor.Gray],
    [LocationType.WRECK, MarkerColor.Blue],
    [LocationType.REEF, MarkerColor.LightBlue],
    [LocationType.JETTY, MarkerColor.LightBlue],
    [LocationType.MUCK, MarkerColor.LightBlue],
    [LocationType.SAND, MarkerColor.LightBlue],
    [LocationType.OCEAN_WALL, MarkerColor.LightBlue],
    [LocationType.CAMPSITE, MarkerColor.Brown],
]);

export const LocationTypeToIcon = new Map<LocationType, string>([
    [LocationType.UNKNOWN, "question"],
    [LocationType.DEPRESSION, "leaf"],
    [LocationType.NOT_FOUND, "cross"],
    [LocationType.CAVE, "hammer"],
    [LocationType.SINKHOLE, "circle"],
    [LocationType.SOLUTION_TUBE, "circle"],
    [LocationType.ROCKY_OUTCROP, "gears"],
    [LocationType.DESTROYED, "cross"],
    [LocationType.LANDMARK, "info"],
    [LocationType.MINE, "truck-pickup"],
    [LocationType.WRECK, "ship"],
    [LocationType.REEF, "fish"],
    [LocationType.JETTY, "fish"],
    [LocationType.MUCK, "fish"],
    [LocationType.SAND, "fish"],
    [LocationType.OCEAN_WALL, "fish"],
    [LocationType.CAMPSITE, "fire"],
]);

export function getLocationTypeColor(type: LocationType | keyof typeof LocationType) {
    return LocationTypeToColor.get(type as LocationType);
}

export function LocationTypeToDisplayString(type: LocationType | undefined) {
    if (type) {
        switch (type) {
            case LocationType.UNKNOWN:
                return "Unknown type";
            case LocationType.CAVE:
                return "Cave";
            case LocationType.DEPRESSION:
                return "Depression";
            case LocationType.DESTROYED:
                return "Destroyed";
            case LocationType.NOT_FOUND:
                return "Not found";
            case LocationType.ROCKY_OUTCROP:
                return "Rocky outcrop";
            case LocationType.SINKHOLE:
                return "Sinkhole";
            case LocationType.SOLUTION_TUBE:
                return "Solution tube";
            case LocationType.WRECK:
                return "Wreck";
            case LocationType.REEF:
                return "Reef";
            case LocationType.SAND:
                return "Sand";
            case LocationType.MUCK:
                return "Muck";
            case LocationType.JETTY:
                return "Jetty";
            case LocationType.MINE:
                return "Mine";
        }
    }
    return "";
}

@customElement("ds-place-marker")
export class PlaceMarker extends BaseElement implements ChangeSetListener {
    static override styles = [styles];

    @property({ type: String }) placeId?: string;

    @state() protected _text?: string;
    @state() protected _type?: LocationType;
    @state() protected _color?: string;
    @state() protected _diveRating?: string;
    @state() protected _waterTemp?: string;
    @state() protected _depth?: string;
    @state() protected _warningLevels: WarningLevel[] = [];

    @watch("placeId")
    async onPlaceChanged() {
        this._text = undefined;
        this._color = undefined;
        this._type = undefined;
        this._diveRating = undefined;
        this._waterTemp = undefined;
        this._depth = undefined;
        this._warningLevels = [];

        if (this.placeId) {
            const place = await this._kernel.dataStore.getSource("places").read(this.placeId);
            if (place) {
                this._color = getLocationTypeColor(place.data.spelioData.type);
                this._text = place.data.name;
                this._type = place.data.spelioData.type;
                this._diveRating = getPlaceDiveRating(place);
                this._waterTemp = getPlaceWaterTempText(place);
                this._depth = getPlaceWaterDepthText(place);

                // show the types of warnings this place has but not the details
                const placeWarnings = getPlaceWarnings(place);
                if (placeWarnings.length > 0) {
                    this._warningLevels = uniq(
                        placeWarnings
                            .map((x) => getWarning(x))
                            .filter((x) => x)
                            .map((x: any) => x.level)
                    );
                }
            }
        }
    }

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._kernel.dataStore.connectClient(
            new DataClient(this, [{ source: "warnings", paths: [] }])
        );
    }

    public override disconnectedCallback(): void {
        super.disconnectedCallback();
        this._kernel.dataStore.disconnectClient(this);
    }

    onChangeSet(provider: DataProvider, _changeSet: ChangeSet<any>): void {
        if (provider.name === "warnings") {
            this.onPlaceChanged();
        }
    }

    override render() {
        return html`<div class="div-icon-inner">
            <sl-icon name="caret-up-fill"></sl-icon>
            <div class="body">
                <div class="row">
                    <div class="flex-column header">
                        <div class="div-icon-circle" style="background-color:${this._color}"></div>
                        ${litIf(
                            this._text &&
                                this._text.length > 0 &&
                                this._text.toLowerCase() !== "unnamed",
                            () => html`<span class="info-title">${this._text}</span>`
                        )}
                        ${litIf(
                            this._type && this._type.length > 0,
                            () =>
                                html` <div class="row type-row">
                                    <span class="info-type"
                                        >${LocationTypeToDisplayString(this._type)}</span
                                    >
                                </div>`
                        )}
                        ${litIf(
                            this._diveRating,
                            () =>
                                html` <div class="row type-row">
                                    <span class="info-type">${this._diveRating} level</span>
                                </div>`
                        )}
                        ${litIf(
                            this._depth,
                            () =>
                                html` <div class="row type-row">
                                    <span class="info-type">Depth ${this._depth}</span>
                                </div>`
                        )}
                        ${litIf(
                            this._waterTemp,
                            () =>
                                html` <div class="row type-row">
                                    <span class="info-type">${this._waterTemp}</span>
                                </div>`
                        )}
                        ${litIf(
                            this._warningLevels.length > 0,
                            () =>
                                html` <div class="row type-row warning-row">
                                    ${this._warningLevels.map(
                                        (level) =>
                                            html`<div class="warning-icon">
                                                <ds-warning-icon .level=${level}></ds-warning-icon>
                                            </div>`
                                    )}
                                </div>`
                        )}
                    </div>
                </div>
            </div>
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-place-marker": PlaceMarker;
    }
}
